import React, { useCallback, useMemo } from 'react'

import { action } from 'mobx'

import {
  useDeleteNotificationCustomFilterMutation,
  useGetNotificationCustomFiltersByProjectQuery,
  useSaveNotificationCustomFilterMutation,
} from '~/client/graph/operations/generated/NotificationCustomFilters.generated'
import {
  DeleteFilterMutationFn,
  FilterTypeDescriptor,
  SaveFilterMutationFn,
} from '~/client/src/desktop/components/SavedViews'
import type { IDrillDownFilterCategory } from '~/client/src/shared/components/DrillDownFilter/DrillDownFilter.types'
import { getField } from '~/client/src/shared/lists/core/schema'
import {
  notificationFilterSetsToSavedViewFilterSets,
  notificationSavedViewFilterTypeForFieldKey,
  NOTIFICATION_FILTER_FIELDS,
  stripKnownNotificationFilterPrefix,
} from '~/client/src/shared/lists/notifications/notificationFilterVocabulary'
import type { IFilterItem } from '~/client/src/shared/lists/notifications/notificationListFilterCategories'
import { applyNotificationSavedView } from '~/client/src/shared/lists/notifications/notificationSavedViewAdapter'
import { NotificationsListStore } from '~/client/src/shared/lists/notifications/NotificationsList.store'

interface UseNotificationsSavedViewsParams {
  projectId: string
  userId: string
  notificationsListStore: NotificationsListStore
  categories: IDrillDownFilterCategory<IFilterItem>[]
}

export const useNotificationsSavedViews = ({
  projectId,
  userId,
  notificationsListStore,
  categories,
}: UseNotificationsSavedViewsParams) => {
  const { data: savedViewsData } =
    useGetNotificationCustomFiltersByProjectQuery({
      variables: { projectId },
      skip: !projectId,
    })

  const savedViews = useMemo(() => {
    return (
      savedViewsData?.notificationCustomFilters?.data
        ?.filter(filter => filter.isPublic || filter.userId === userId)
        ?.map(filter => ({
          id: filter.id,
          name: filter.name,
          isPublic: filter.isPublic,
          filtersByFilterType: filter.filtersByFilterType,
          groupBy: filter.groupBy,
          userId: filter.userId,
        })) || []
    )
  }, [savedViewsData, userId])

  // Build facets with renderFilterValue from DrillDownFilter categories
  const facets = useMemo((): FilterTypeDescriptor[] => {
    const labelsByCategoryId = new Map<string, Map<string, string>>()
    for (const cat of categories) {
      const labels = new Map<string, string>()
      for (const item of cat.items) {
        const prefixedId = cat.getItemId(item)
        const displayName = cat.getItemLabel(item)
        const colonIdx = prefixedId.indexOf(':')
        const unprefixed =
          colonIdx > 0 ? prefixedId.slice(colonIdx + 1) : prefixedId
        labels.set(prefixedId, displayName)
        labels.set(unprefixed, displayName)
      }
      labelsByCategoryId.set(cat.id, labels)
    }

    const facetDefs = NOTIFICATION_FILTER_FIELDS.map(field => ({
      key: notificationSavedViewFilterTypeForFieldKey(field.key),
      label: getField(notificationsListStore.list.schema, field.key)?.label,
    }))

    return facetDefs.map(({ key, label }) => {
      const labels = labelsByCategoryId.get(key)
      return {
        key,
        label: label || key,
        renderFilterValue: (value: unknown): JSX.Element | null => {
          const rawValue = String(value)
          const displayName =
            labels?.get(rawValue) ||
            labels?.get(stripKnownNotificationFilterPrefix(rawValue, key)) ||
            rawValue
          return React.createElement(
            'div',
            {
              key: `${key}-${value}`,
              className:
                'tw:inline-flex tw:text-body-sm tw:text-foreground tw:overflow-hidden tw:whitespace-nowrap tw:text-ellipsis tw:max-w-[270px]',
            },
            displayName,
          )
        },
      }
    })
  }, [categories, notificationsListStore])

  const appliedFilters = useMemo(() => {
    return notificationFilterSetsToSavedViewFilterSets(
      notificationsListStore.appliedFilterSets,
    )
  }, [notificationsListStore, notificationsListStore.appliedFilterSets])

  const handleApplyFilters = useCallback(
    action(
      (filtersToApply: { [key: string]: Set<string> }, groupBy: string) => {
        applyNotificationSavedView(
          notificationsListStore,
          filtersToApply,
          groupBy,
        )
      },
    ),
    [notificationsListStore],
  )

  const [saveNotificationFilter] = useSaveNotificationCustomFilterMutation()
  const [deleteNotificationFilter] = useDeleteNotificationCustomFilterMutation()

  const saveFilterMutation: SaveFilterMutationFn = useCallback(
    async options => saveNotificationFilter(options as any),
    [saveNotificationFilter],
  )

  const deleteFilterMutation: DeleteFilterMutationFn = useCallback(
    async options => deleteNotificationFilter(options as any),
    [deleteNotificationFilter],
  )

  return {
    savedViews,
    appliedFilters,
    handleApplyFilters,
    facets,
    saveFilterMutation,
    deleteFilterMutation,
  }
}
